import { getProvider } from "@aux0/connectors";
import { useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { parseAsArrayOf, parseAsString, useQueryState } from "nuqs";
import { WeekGrid } from "@/components/calendar/week-grid";
import { Tag } from "@/components/common/badges";
import { Page, PageTitle, Panel } from "@/components/common/page";
import { Stat } from "@/components/common/stat";
import { EmptyState, PageSkeleton } from "@/components/common/states";
import { Checkbox } from "@/components/ui/checkbox";
import { q } from "@/data/queries";
import { formatDayShort, formatNumber } from "@/lib/format";

/**
 * The calendar week.
 *
 * The product sells calendar and the mock app had no screen for it, which left
 * both the dashboard and the marketing site with a hole where a third of the
 * launch scope should be. This is that screen: one week, every connected
 * calendar, two providers' events in one grid.
 *
 * Which calendars are shown lives in the query string, so a support link can
 * open exactly the subset someone is asking about — and so the screenshot of a
 * single calendar is a URL rather than a code change.
 */
export const Route = createFileRoute("/p/$project/calendar/")({
  loader: async ({ context }) => {
    const [week] = await Promise.all([
      context.queryClient.ensureQueryData(q.calendarWeek()),
      context.queryClient.ensureQueryData(q.calendars()),
      context.queryClient.ensureQueryData(q.calendarEvents({ includeCancelled: true })),
      context.queryClient.ensureQueryData(q.calendarBusy()),
    ]);
    return { week };
  },
  pendingComponent: PageSkeleton,
  component: CalendarPage,
});

function CalendarPage() {
  const { project } = Route.useParams();
  const { week } = Route.useLoaderData();
  const { data: calendars } = useSuspenseQuery(q.calendars());
  const { data: events } = useSuspenseQuery(
    q.calendarEvents({ includeCancelled: true }),
  );
  const { data: busy } = useSuspenseQuery(q.calendarBusy());

  // The default is the calendars the user has not hidden at the provider, which
  // is what `selected` means there. `cal=` overrides it.
  const defaultIds = calendars
    .filter((calendar) => calendar.selected)
    .map((calendar) => calendar.id);
  const [chosen, setChosen] = useQueryState(
    "cal",
    parseAsArrayOf(parseAsString).withOptions({ history: "replace", scroll: false }),
  );
  const shown = chosen ?? defaultIds;

  const visible = events.filter((event) => shown.includes(event.calendarId));
  const visibleBusy = busy.filter((block) => shown.includes(block.calendarId));
  const unanswered = visible.filter(
    (event) => event.selfResponse === "needs_action",
  ).length;
  const viaApi = visible.filter((event) => event.viaApi).length;
  const freeBusyOnly = calendars.filter(
    (calendar) => calendar.access === "free_busy" && shown.includes(calendar.id),
  );

  const toggle = (id: string, on: boolean) => {
    const next = on ? [...shown, id] : shown.filter((value) => value !== id);
    void setChosen(
      next.length === defaultIds.length && next.every((v) => defaultIds.includes(v))
        ? null
        : next,
    );
  };

  return (
    <Page wide>
      <PageTitle
        title="Calendar"
        description={`Every connected calendar in one week, ${formatDayShort(week.from)} to ${formatDayShort(
          new Date(Date.parse(week.to) - 86_400_000).toISOString(),
        )}. Google and Outlook events behind one object model, with the colour coming from the calendar rather than the provider.`}
      />

      <div className="grid grid-cols-2 gap-px overflow-hidden rounded-lg border bg-border lg:grid-cols-4">
        <Stat
          label="Events this week"
          value={formatNumber(visible.filter((e) => e.status !== "cancelled").length)}
          footnote={`${formatNumber(visible.filter((e) => e.status === "cancelled").length)} cancelled`}
        />
        <Stat
          label="Awaiting an answer"
          value={unanswered}
          tone={unanswered > 0 ? "warn" : undefined}
          footnote={unanswered > 0 ? "The account has not responded" : "All answered"}
        />
        <Stat
          label="Created through the API"
          value={viaApi}
          footnote="Invite, Meet link and hold, all ours"
        />
        <Stat
          label="Calendars"
          value={shown.length}
          unit={`/ ${calendars.length}`}
          footnote={
            freeBusyOnly.length > 0
              ? `${freeBusyOnly.length} free/busy only`
              : undefined
          }
        />
      </div>

      <div className="grid gap-4 xl:grid-cols-[minmax(0,1fr)_260px]">
        <div className="min-w-0">
          {shown.length === 0 ? (
            <EmptyState
              title="No calendars selected"
              description="Tick a calendar to put its events back on the grid."
              className="rounded-lg border bg-card"
            />
          ) : (
            <WeekGrid
              from={week.from}
              events={visible}
              busy={visibleBusy}
              calendars={calendars}
              project={project}
            />
          )}
        </div>

        <div className="flex flex-col gap-4">
          <Panel title="Calendars" bodyClassName="flex flex-col gap-0.5 p-2">
            {calendars.map((calendar) => {
              const id = `cal-${calendar.id}`;
              return (
                <label
                  key={calendar.id}
                  htmlFor={id}
                  className="flex cursor-pointer items-start gap-2.5 rounded-md px-2 py-1.5 transition-colors hover:bg-accent/50 has-focus-visible:ring-2 has-focus-visible:ring-ring/50"
                >
                  <Checkbox
                    id={id}
                    checked={shown.includes(calendar.id)}
                    onCheckedChange={(value) => toggle(calendar.id, value === true)}
                    className="mt-0.5"
                    style={{ color: calendar.color }}
                  />
                  <span className="min-w-0 flex-1">
                    <span className="flex items-center gap-1.5">
                      <span
                        aria-hidden
                        className="size-2 shrink-0 rounded-[3px]"
                        style={{ backgroundColor: calendar.color }}
                      />
                      <span className="min-w-0 truncate text-[12.5px] font-medium">
                        {calendar.name}
                      </span>
                    </span>
                    <span className="mt-0.5 block truncate text-[11px] text-muted-foreground">
                      {getProvider(calendar.provider)?.name ?? calendar.provider}
                      {calendar.access === "free_busy"
                        ? " · free/busy only"
                        : calendar.access === "reader"
                          ? " · read only"
                          : ""}
                    </span>
                  </span>
                </label>
              );
            })}
          </Panel>

          {freeBusyOnly.length > 0 ? (
            <Panel
              title="What you cannot see"
              bodyClassName="flex flex-col gap-2 px-4 py-3"
            >
              {freeBusyOnly.map((calendar) => (
                <p
                  key={calendar.id}
                  className="text-[12px] leading-[1.5] text-muted-foreground"
                >
                  <span className="font-medium text-foreground">{calendar.name}</span>{" "}
                  only answers free/busy, so the grid shows a hatched band where it is
                  busy and nothing else. Aux0 returns the same thing the API does rather
                  than inventing a title.
                </p>
              ))}
            </Panel>
          ) : null}

          <Panel title="The shape a provider shares" bodyClassName="px-4 py-3">
            <ul className="flex flex-col gap-2 text-[12px] leading-[1.5] text-muted-foreground">
              <li>
                An all-day event's <span className="font-mono">start</span> and{" "}
                <span className="font-mono">end</span> are dates, not instants, and{" "}
                <span className="font-mono">end</span> is exclusive. Every provider
                agrees on this and every naive client gets it wrong.
              </li>
              <li>
                A cancelled event stays in the window as a tombstone{" "}
                <Tag mono>cancelled</Tag> so a sync cannot resurrect it.
              </li>
              <li>
                A recurring instance carries{" "}
                <span className="font-mono">recurringEventId</span>; only the master
                carries the <span className="font-mono">RRULE</span>.
              </li>
            </ul>
          </Panel>
        </div>
      </div>
    </Page>
  );
}
